import React, { useState } from "react";
import { Button, Message, Input, Upload } from "../../../BP2Components";
import { useDispatch } from "react-redux";
import { setWidgetInSheetParams } from "appRedux/actions/Actions";
import IntlMessages from "@util/IntlMessages";
import { SketchPicker } from "react-color";
import { sendFileToCloudStorage } from "services/restAPI/RestApi";
import { useIntl } from "react-intl";
import { getBase64StringFromDataURL } from "../../../../util/Scripts";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const NewsletterGraphSettings = ({ value, setWidgetSettings }) => {
   const dispatch = useDispatch();
   const intl = useIntl();

   const [settings, setSettings] = useState({
      ...value,
      series: value.series || [],
   });

   const [displayColorPicker, setDisplayColorPicker] = useState<number | null>(null);

   const handleUploadFile = (info) => {
      if (!info.type.startsWith("image/")) {
         Message.error(intl.formatMessage({ id: "forms.wrongFileType" }));
         return;
      }

      const cleanedType = info.type.replace(/^image\//, "");
      const reader = new FileReader();

      reader.onload = function (loadEvent: any) {
         const result = loadEvent.target.result;
         const base64 = getBase64StringFromDataURL(result);
         if (base64) {
            Message.loading(intl.formatMessage({ id: "global.loading" }));
            try {
               sendFileToCloudStorage(info.name, cleanedType, base64).then((res) => {
                  const newSettings = {
                     ...settings,
                     imageUrl: res?.data?.cloudStorage?.url,
                  };
                  updateSettings(newSettings);
               });
               Message.success(`${intl.formatMessage({ id: "forms.uploadSucces" })} ${info.name}`);
            } catch (error) {
               console.log(error);
               Message.error(intl.formatMessage({ id: "forms.uploadFailed" }));
            } finally {
               Message.destroy();
            }
         }
      };
      reader.readAsDataURL(info);
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      const finalSettings = {
         ...settings,
         listId: settings.listId ? String(settings.listId) : null,
         axisXHumanName: settings.axisXHumanName ? String(settings.axisXHumanName) : null,
      };
      updateSettings(finalSettings);
      Message.success(intl.formatMessage({ id: "global.saved" }));
   };

   const handleDeletePhoto = () => {
      const newSettings = {
         ...settings,
         imageUrl: null,
      };
      updateSettings(newSettings);
   };

   const handleChange = (e) => {
      const { name, value: val } = e.target;
      const newSettings = { ...settings, [name]: val };
      setSettings(newSettings);
      
      // Dla innych pól niż listId synchronizujemy od razu z Redux i podglądem
      if (name !== "listId") {
         const finalSettings = {
            ...newSettings,
            listId: newSettings.listId ? String(newSettings.listId) : null,
            axisXHumanName: newSettings.axisXHumanName ? String(newSettings.axisXHumanName) : null,
         };
         updateSettings(finalSettings);
      }
   };

   const updateSettings = (updatedSettings) => {
      setSettings(updatedSettings);
      dispatch(setWidgetInSheetParams(updatedSettings));
      if (setWidgetSettings) setWidgetSettings(updatedSettings);
   };

   const addSerie = () => {
      const newSettings = {
         ...settings,
         series: [...settings.series, { humanId: "", label: "", color: "#0C5B3A" }],
      };
      updateSettings(newSettings);
   };

   const removeSerie = (index) => {
      const newSeries = [...settings.series];
      newSeries.splice(index, 1);
      const newSettings = { ...settings, series: newSeries };
      updateSettings(newSettings);
   };

   const handleSerieChange = (index, key, val) => {
      const newSeries = [...settings.series];
      newSeries[index] = { ...newSeries[index], [key]: val };
      const newSettings = { ...settings, series: newSeries };
      updateSettings(newSettings);
   };

   const togglePicker = (index) => {
      setDisplayColorPicker(displayColorPicker === index ? null : index);
   };

   const handleColorChange = (index, color) => {
      const newSeries = [...settings.series];
      newSeries[index] = { ...newSeries[index], color: color.hex };
      const newSettings = { ...settings, series: newSeries };
      updateSettings(newSettings);
   };

   const handleEditListBuilder = () => {
      if (settings.listId) {
         window.open(`/listbuilder?listId=${settings.listId}`, "_blank");
      } else {
         Message.warning("Proszę wpisać List ID");
      }
   };

   return (
      <form onSubmit={handleSubmit}>
         <div className="newsletter-graph-settings">
            <div className="newsletter-graph-settings__inner">
               <label className="newsletter-graph-settings__inner__label">
                  <IntlMessages id="settings.title" /> (titleKey):
                  <Input
                     type="text"
                     name="titleKey"
                     onChange={handleChange}
                     value={settings.titleKey}
                  />
               </label>

               <div className="newsletter-graph-settings__inner__label">
                  <IntlMessages id="settings.upload" /> (imageUrl):
                  <div style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "flex-end", flex: 1 }}>
                     <Upload onUpload={handleUploadFile} disabled={settings.imageUrl}>
                        <Button buttonType={settings.imageUrl ? "disabled" : "default"}>
                           <IntlMessages id="settings.upload" />
                        </Button>
                     </Upload>
                     <Button
                        onClick={handleDeletePhoto}
                        buttonType={settings.imageUrl ? "default" : "disabled"}
                     >
                        <IntlMessages id="delete" />
                     </Button>
                     {settings.imageUrl && (
                        <img src={settings.imageUrl} alt="" style={{ maxWidth: "50px", maxHeight: "50px" }} />
                     )}
                  </div>
               </div>

               <div className="newsletter-graph-settings__inner__label">
                  List ID:
                  <div style={{ display: "flex", gap: "10px", width: "100%", alignItems: "center" }}>
                     <Input
                        type="text"
                        name="listId"
                        onChange={handleChange}
                        value={settings.listId}
                        style={{ flex: 1, marginBottom: 0 }}
                     />
                     <Button
                        buttonType="primary"
                        style={{ whiteSpace: "nowrap" }}
                        onClick={() => {
                           const finalSettings = {
                              ...settings,
                              listId: settings.listId ? String(settings.listId) : null,
                              axisXHumanName: settings.axisXHumanName ? String(settings.axisXHumanName) : null,
                              refreshTrigger: Date.now()
                           };
                           updateSettings(finalSettings);
                        }}
                     >
                        Wczytaj listę
                     </Button>
                     <Button onClick={handleEditListBuilder} style={{ whiteSpace: "nowrap" }}>
                        <IntlMessages id="global.edit" />
                     </Button>
                  </div>
               </div>

               <label className="newsletter-graph-settings__inner__label">
                  X Axis Human Name (axisXHumanName):
                  <Input
                     type="text"
                     name="axisXHumanName"
                     onChange={handleChange}
                     value={settings.axisXHumanName}
                  />
               </label>

               <div className="newsletter-graph-settings__inner__label">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                     <span>Series List:</span>
                     <Button
                        buttonType="primary"
                        onClick={addSerie}
                        size="small"
                        className="newsletter-graph-settings__inner__add-btn"
                     >
                        <PlusOutlined />
                        <span style={{ marginLeft: "8px" }}>Add Serie</span>
                     </Button>
                  </div>
               </div>
               {settings.series.map((serie, index) => (
                  <div key={index} style={{ border: "1px solid #d9d9d9", padding: "10px", marginBottom: "20px", borderRadius: "4px" }}>
                        <div style={{ display: "flex", gap: "10px", marginBottom: "5px" }}>
                           <Input
                              placeholder="Label"
                              value={serie.label}
                              onChange={(e) => handleSerieChange(index, "label", e.target.value)}
                           />
                           <Input
                              placeholder="humanId"
                              value={serie.humanId}
                              onChange={(e) => handleSerieChange(index, "humanId", e.target.value)}
                           />
                           <Button
                              buttonType="danger"
                              onClick={() => removeSerie(index)}
                              className="newsletter-graph-settings__inner__delete-btn"
                           >
                              <DeleteOutlined />
                           </Button>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                           <div
                              onClick={() => togglePicker(index)}
                              style={{
                                 width: "36px",
                                 height: "14px",
                                 borderRadius: "2px",
                                 background: serie.color || "#0C5B3A",
                                 cursor: "pointer",
                                 border: "1px solid #d9d9d9",
                              }}
                           />
                           {displayColorPicker === index && (
                              <div style={{ position: "absolute", zIndex: "2" }}>
                                 <div
                                    style={{ position: "fixed", top: "0px", right: "0px", bottom: "0px", left: "0px" }}
                                    onClick={() => setDisplayColorPicker(null)}
                                 />
                                 <SketchPicker
                                    color={serie.color || "#0C5B3A"}
                                    onChange={(color) => handleColorChange(index, color)}
                                 />
                              </div>
                           )}
                           <span>Color</span>
                        </div>
                     </div>
                  ))}

               <div className="gx-mt-4">
                  <Button buttonType="primary" htmlType="submit">
                     <IntlMessages id="global.save" />
                  </Button>
               </div>
            </div>
         </div>
      </form>
   );
};

export default NewsletterGraphSettings;
